import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Image, Select, Spin, message, ConfigProvider, Empty } from "antd";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import { constants, INVENTORY_WASTAGE_TYPE, ROLES } from "../../../constant/constants";
import InventoryTable from "../../../component/inventory/InventoryTable";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import InventoryConfirmModal from "../../../component/inventory/InventoryConfirmModal";
import CustomLoader from "../../../component/menu/CustomLoader";

export default function WastageList() {
  const { restaurantId, user, showNotification, role, setNetworkCall } = useUserStore();

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [dataLoading, setDataLoading] = useState(true);
  const [wastageData, setWastageData] = useState();
  const [selectedDeleteId, setSelectedDeleteId] = useState("");
  const [filters, setFilters] = useState({
    restaurantId,
    searchKey: "",
    wastageType: "",
    page: 1,
    perPage: 10,
  });
  const [openDelModal, setOpenDelModal] = useState(false);

  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);
    const params = { ...filters, page, perPage: pageSize };
    getWastageList(params);
  }

  async function getWastageList(params = null) {
    if (!params?.restaurantId) {
      console.error("Restaurant id is required for getting recipe list.");
      return;
    }

    setDataLoading(true);

    try {
      const res = await ApiService.GET_INVENTORY_WASTAGE_LIST(params || filters, user.token.token);

      if (res.code !== 200) {
        showNotification(res.data?.message || "Failed to fetch wastage list.");
        return;
      }

      const { payload } = res || {};
      if (payload) {
        const { page, perPage, total, records } = payload;
        setPage(page);
        setPerPage(perPage);
        setTotal(total);
        setWastageData(records);
      }
    } catch (err) {
      showNotification(err?.response?.data?.message || "An error occurred.");
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => {
    getWastageList({ restaurantId });
  }, [restaurantId]);

  function transformData(wastageData) {
    if (!Array.isArray(wastageData) || wastageData.length < 1) return [];

    return [
      ...wastageData.map((item, index) => ({
        // id: index + 1,
        id: item.wastageId,
        // wastageId: item.wastageId,
        wastageType:
          item.wastageType === "INGREDIENT"
            ? "Ingredient"
            : item.wastageType === "FOOD"
            ? "Food"
            : item.wastageType === "ADD_ONS"
            ? "Add-ons"
            : "Unknown",
        ingredientPerItem: item.wastageItem?.name || "N/A",
        wastage: parseFloat(item.wastageQuantity) || 0,
        costPerUnit: parseFloat(item.costPerUnit) || 0,
        totalAmount: parseFloat(item.totalAmount) || 0,
        wastageReason: item.wastageReason || "N/A",
        date: new Date(item.wastageDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }),
      })),
    ];
  }

  function onHandleEditItem(_id) {
    console.log("__edit__item", _id);
    navigate(`/inventory/wastage/${_id}/edit`);
  }

  function onHandleDeleteItem(_id) {
    /**
     * show warning for
     * if user role is staff then user can not delete item
     */
    if (role === ROLES.STAFF) {
      showNotification("You are not authorize to delete this ingredient.");
      return;
    }

    setOpenDelModal(true);
    setSelectedDeleteId(_id);
  }

  function onHandleSearchText(e) {
    const val = e.target.value;
    setFilters((prev) => ({ ...prev, searchKey: val, page: 1 }));
  }

  function onHandleSearch() {
    
    getWastageList({ ...filters, searchKey: filters.searchKey.trim() });
  }

  function onHandleWastageType(val) {
    setFilters((prev) => ({ ...prev, wastageType: val, page: 1 }));
  }

  function clearFilterFields() {
    const defaultFilters = {
      ...filters,
      searchKey: "",
      wastageType: "",
      page: 1,
    };
    setFilters((prev) => ({ ...prev, ...defaultFilters }));
  }

  function onHandleClear() {
    clearFilterFields();
    getWastageList({ restaurantId });
  }

  // delete modal confirm handler
  async function onHandleDeleteRecipeConfirm() {
    try {
      setNetworkCall(true);
      const res = await ApiService.INVENTORY_WASTAGE_ITEM_DELETE(selectedDeleteId, user.token.token);
      setOpenDelModal(false);
      setNetworkCall(false);

      setSelectedDeleteId("");

      if (res.code !== 200) {
        setOpenDelModal(false);
        showNotification(res.data.message);
        return;
      }
      const params = {
        restaurantId: restaurantId,
      };

      await getWastageList(params);
      showNotification("Wastage item deleted successfully.", false);
      return;
    } catch (err) {
      setOpenDelModal(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  function closeConfirmationModal() {
    setOpenDelModal(false);
    setSelectedDeleteId("");
  }

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_7"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <h1 className="pt-[26px] pb-[20px] font-bold text-black text-[24px] leading-[24px] cursor-default">
              Wastage List
            </h1>
            {/* filter section */}
            <ConfigProvider
              theme={{
                components: {
                  Select: {
                    zIndexPopup: 1,
                  },
                },
              }}
            >
              <div className="flex items-start justify-between">
                <div className="mb-[20px] flex items-center space-x-4 w-full">
                  <div className="min-w-[332px] h-[48px]">
                    <Input
                      name="search"
                      className={"w-full h-full"}
                      value={filters.searchKey}
                      onChange={onHandleSearchText}
                      onPressEnter={onHandleSearch}
                      placeholder="Search keyword"
                    />
                  </div>
                  <div className="min-w-[164px] h-[48px]">
                    <Select
                      value={filters.wastageType || null}
                      name="wastageType"
                      style={{ width: "100%", height: "100%" }}
                      onChange={onHandleWastageType}
                      options={INVENTORY_WASTAGE_TYPE}
                      placeholder="Wastage Type"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      className="w-[100px] h-[48px] bg-black rounded-[5px] text-white font-bold leading-[16px]"
                      onClick={onHandleClear}
                    >
                      Clear
                    </button>
                    <button
                      className="w-[100px] h-[48px] bg-[#FCBD10] rounded-[5px] text-black font-bold leading-[16px]"
                      onClick={onHandleSearch}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <button
                  className="w-[104px] h-[48px] bg-[#FCBD10] rounded-[5px] text-black font-bold leading-[16px]"
                  onClick={() => navigate("/inventory/wastage/add")}
                >
                  <span className="text-[15px] font-bold">Add</span>
                  <Image
                    src="https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/add.svg"
                    height={15.6}
                    width={15.6}
                    preview={false}
                    alt="add"
                    className="inline-block ml-2"
                  />
                </button>
              </div>
            </ConfigProvider>

            {/* filter section ends */}
            <div className="min-h-[90vh]">
              {!dataLoading && wastageData.length > 0 && (
                <InventoryTable
                  data={transformData(wastageData)}
                  coloredCols={["SN", "Wastage Type", "Wastage Reason", "Date"]}
                  handleEdit={onHandleEditItem}
                  handleDelete={onHandleDeleteItem}
                  paginationRecords={{
                    handlePageChange: onHandlePageChange,
                    page,
                    perPage,
                    setPerPage,
                    total,
                  }}
                />
              )}

              {!dataLoading && wastageData.length === 0 && (
                <div className="h-[50vh] flex items-center justify-center">
                  <Empty description={"No Data Found!"} />
                </div>
              )}

              {dataLoading && (
                <div className="h-[50vh] flex items-center justify-center">
                  <Spin size="large" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <InventoryConfirmModal
        open={openDelModal}
        title="Are you sure?"
        message="This will remove the wastage permanently."
        onConfirm={onHandleDeleteRecipeConfirm}
        onCancel={closeConfirmationModal}
      />
    </div>
  );
}
